import {
  ActionIcon,
  Avatar,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconPlus,
  IconTrash,
  IconExternalLink,
  IconGripVertical,
  IconPhoto,
  IconUpload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import type { MedplumClient } from '@medplum/core';
import { useRef, useState } from 'react';
import { PortalTile } from '../../users/types/rbac';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useResolvedImageUrl } from '../../../hooks/useResolvedImageUrl';
import { normalizeExternalUrl } from '../../../lib/normalizeExternalUrl';
import { attachmentDataUrl, createSecuredAttachment } from '../../../lib/createSecuredAttachment';

interface PortalTabProps {
  medplum: MedplumClient;
  organizationId: string;
  tiles: PortalTile[];
  onUpdateTiles: (tiles: PortalTile[]) => void;
  onMarkChanged: () => void;
}

function TileAvatar({ imageUrl }: { imageUrl?: string }) {
  const resolvedUrl = useResolvedImageUrl(imageUrl);

  return (
    <Avatar size="lg" radius="md" src={resolvedUrl} color="gray">
      <IconPhoto size={24} />
    </Avatar>
  );
}

interface DraggableTileProps {
  tile: PortalTile;
  index: number;
  moveTile: (dragIndex: number, hoverIndex: number) => void;
  onEdit: (tile: PortalTile) => void;
  onDelete: (id: string) => void;
}

const DraggableTile = ({
  tile,
  index,
  moveTile,
  onEdit,
  onDelete,
}: DraggableTileProps) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'TILE',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'TILE',
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        moveTile(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <Card
      ref={(node) => drag(drop(node))}
      withBorder
      p="lg"
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
      }}
    >
      <Group gap="lg" align="flex-start">
        <ActionIcon variant="subtle" style={{ cursor: 'grab' }}>
          <IconGripVertical size={20} />
        </ActionIcon>

        <TileAvatar imageUrl={tile.imageUrl} />

        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
          <Text fw={500} size="sm" truncate>
            {tile.title}
          </Text>
          <Group gap={4}>
            <Text
              size="xs"
              c="blue"
              component="a"
              href={normalizeExternalUrl(tile.link)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              {tile.link}
            </Text>
            <IconExternalLink size={12} />
          </Group>
        </Stack>

        <Group gap="xs">
          <Button variant="default" size="xs" onClick={() => onEdit(tile)}>
            Edit
          </Button>
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => onDelete(tile.id)}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Group>
    </Card>
  );
};

export function PortalTab({
  medplum,
  organizationId,
  tiles,
  onUpdateTiles,
  onMarkChanged,
}: PortalTabProps) {
  const [showAddTile, setShowAddTile] = useState(false);
  const [editingTile, setEditingTile] = useState<PortalTile | null>(null);
  const [newTile, setNewTile] = useState<Partial<PortalTile>>({
    title: '',
    link: '',
    imageUrl: '',
  });
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const previewImageUrl = useResolvedImageUrl(newTile.imageUrl);

  const moveTile = (dragIndex: number, hoverIndex: number) => {
    const updatedTiles = [...tiles];
    const [removed] = updatedTiles.splice(dragIndex, 1);
    updatedTiles.splice(hoverIndex, 0, removed);

    // Update order
    const reorderedTiles = updatedTiles.map((tile, idx) => ({
      ...tile,
      order: idx,
    }));

    onUpdateTiles(reorderedTiles);
    onMarkChanged();
  };

  const handleAddTile = () => {
    if (newTile.title && newTile.link) {
      const tile: PortalTile = {
        id: `t${Date.now()}`,
        title: newTile.title,
        link: normalizeExternalUrl(newTile.link),
        imageUrl: newTile.imageUrl,
        order: tiles.length,
      };
      onUpdateTiles([...tiles, tile]);
      setNewTile({ title: '', link: '', imageUrl: '' });
      setShowAddTile(false);
      onMarkChanged();
    }
  };

  const handleEditTile = () => {
    if (editingTile && newTile.title && newTile.link) {
      const updatedTiles = tiles.map((t) =>
        t.id === editingTile.id
          ? {
            ...t,
            title: newTile.title!,
            link: normalizeExternalUrl(newTile.link!),
            imageUrl: newTile.imageUrl,
          }
          : t
      );
      onUpdateTiles(updatedTiles);
      setEditingTile(null);
      setNewTile({ title: '', link: '', imageUrl: '' });
      onMarkChanged();
    }
  };

  const handleDeleteTile = (id: string) => {
    const updatedTiles = tiles
      .filter((t) => t.id !== id)
      .map((tile, idx) => ({ ...tile, order: idx }));
    onUpdateTiles(updatedTiles);
    onMarkChanged();
  };

  const handleEditClick = (tile: PortalTile) => {
    setEditingTile(tile);
    setNewTile({
      title: tile.title,
      link: tile.link,
      imageUrl: tile.imageUrl,
    });
  };

  const handleCancel = () => {
    setShowAddTile(false);
    setEditingTile(null);
    setNewTile({ title: '', link: '', imageUrl: '' });
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;

    setImageUploading(true);
    try {
      const attachment = await createSecuredAttachment(
        medplum,
        file,
        { reference: `Organization/${organizationId}` },
        file.name,
        file.type,
      );
      const binaryUrl = attachmentDataUrl(attachment);
      setNewTile((prev) => ({ ...prev, imageUrl: binaryUrl }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      notifications.show({ title: 'Could not upload image', message: msg, color: 'red' });
    } finally {
      setImageUploading(false);
    }
  };

  const sortedTiles = [...tiles].sort((a, b) => a.order - b.order);

  return (
    <DndProvider backend={HTML5Backend}>
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={4}>Portal Resources</Title>
            <Text size="sm" c="gray.6" mt="xs">
              Customize resource tiles for your practice portal. Drag to reorder.
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setShowAddTile(true)}
          >
            Add Tile
          </Button>
        </Group>

        {/* Add/Edit Tile Modal */}
        <Modal
          opened={showAddTile || !!editingTile}
          onClose={handleCancel}
          title={editingTile ? 'Edit Tile' : 'Add New Tile'}
          size="md"
          centered
        >
          <Stack gap="md">
            <TextInput
              label="Title"
              placeholder="e.g., Simple Practice"
              value={newTile.title}
              onChange={(e) => setNewTile({ ...newTile, title: e.target.value })}
              required
            />

            <TextInput
              label="Link URL"
              type="url"
              placeholder="google.com or https://example.com"
              value={newTile.link}
              onChange={(e) => setNewTile({ ...newTile, link: e.target.value })}
              required
            />

            <Group gap="md" align="flex-end">
              <Avatar
                size={64}
                radius="md"
                src={previewImageUrl}
                color="gray"
              >
                <IconPhoto size={32} />
              </Avatar>
              <Stack gap="xs" style={{ flex: 1 }}>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => void handleImageFileChange(e)}
                  style={{ display: 'none' }}
                  aria-hidden
                />
                <Button
                  variant="outline"
                  leftSection={imageUploading ? <Loader size={16} /> : <IconUpload size={16} />}
                  fullWidth
                  disabled={imageUploading}
                  onClick={() => imageInputRef.current?.click()}
                >
                  {imageUploading ? 'Uploading…' : 'Upload Image'}
                </Button>
                <TextInput
                  label="Image URL (Optional)"
                  placeholder="Or enter image URL"
                  value={newTile.imageUrl || ''}
                  onChange={(e) =>
                    setNewTile({ ...newTile, imageUrl: e.target.value })
                  }
                />
              </Stack>
            </Group>

            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={editingTile ? handleEditTile : handleAddTile}>
                {editingTile ? 'Save Changes' : 'Add Tile'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Tiles List */}
        {sortedTiles.length === 0 ? (
          <Group justify="center" py="xl">
            <Stack align="center" gap="md">
              <ThemeIcon size={64} radius="md" variant="light" color="gray">
                <IconPhoto size={32} />
              </ThemeIcon>
              <div>
                <Text c="gray.6" size="sm" ta="center">
                  No portal tiles added yet
                </Text>
                <Text c="gray.6" size="xs" ta="center" mt="xs">
                  {`Click "Add Tile" to get started`}
                </Text>
              </div>
            </Stack>
          </Group>
        ) : (
          <Stack gap="xs">
            {sortedTiles.map((tile, index) => (
              <DraggableTile
                key={tile.id}
                tile={tile}
                index={index}
                moveTile={moveTile}
                onEdit={handleEditClick}
                onDelete={handleDeleteTile}
              />
            ))}
          </Stack>
        )}
      </Card>
    </DndProvider>
  );
}
